/**
 * TrackerReadOnlyNote — what the campaign page says about a tracker it cannot
 * fully drive yet. Read-only tracker: the header's "Read-only" chip (compact)
 * and, in the Flows tab, a note where the Flow Optimizer button would be. A
 * tracker whose flows can be edited but has no Flow Optimizer yet (Tres
 * Commas, Phase 2b): no header chip, and "the Flow Optimizer comes next".
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Hourglass, Lock } from 'lucide-react';
import { trackerCan, trackerLabel, type TrackerId } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

interface TrackerReadOnlyNoteProps {
  tracker: TrackerId;
  compact?: boolean;
}

const NOTE_CLASS = 'rounded-md border border-border/60 bg-muted/30 px-2.5 py-1 text-xs';

export const TrackerReadOnlyNote: FC<TrackerReadOnlyNoteProps> = ({ tracker, compact = false }) => {
  const label = trackerLabel(tracker);
  if (trackerCan(tracker, 'flowWrites')) {
    if (compact) return null;
    return (
      <span
        className={cn('inline-flex items-center gap-1.5 text-muted-foreground', NOTE_CLASS)}
        title={`${label} flows can be edited and restored here; the Flow Optimizer comes next`}
        data-tracker={tracker}
      >
        <Hourglass className="h-3 w-3 shrink-0" aria-hidden />
        {`Flow Optimizer for ${label} comes next`}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-muted-foreground',
        compact ? 'text-[11px]' : NOTE_CLASS,
      )}
      title={`${label} campaigns are read-only here`}
      data-tracker={tracker}
    >
      <Lock className="h-3 w-3 shrink-0" aria-hidden />
      {compact ? 'Read-only' : `Read-only · the Flow Optimizer for ${label} comes later`}
    </span>
  );
};

export default TrackerReadOnlyNote;
